import { Box, Grid, GridColumn, Tabs } from "@modernatx-internal/component-library";
import { useRouter } from "next/router";
import React, { useId } from "react";

import { ContentComponentProps, NavBarContent } from "@/content-V2/types";
import { useParseLocalizedPathname } from "@/hooks/useParseLocalizedPathname";

import { useNavBarContext } from "./NavBarContext";
import { PageHeaderScrollable } from "./PageHeader";
import { Sticky, useSticking } from "./Sticky";

const FIRST_ANCHOR_OFFSET = 36;
const ANCHOR_OFFSET = 72;
const NAV_BAR_STICKY_OFFSET: [number, number, number] = [0, 0, 36];

function getAnchorIdFromHref(
  href: string,
  asPath: string,
  getLocalizedPathnamesMatch: (href: string, asPath: string) => boolean
) {
  const [hrefPath, hrefHash] = href.split("#");

  if (!hrefHash) {
    return null;
  }

  if (!hrefPath) {
    return hrefHash;
  }

  const currentPath = asPath.split("#")[0]?.split("?")[0] || asPath;

  return getLocalizedPathnamesMatch(hrefPath, currentPath) ? hrefHash : null;
}

export const NavBarWithContext: React.FC<
  ContentComponentProps<NavBarContent> & { idAttribute: string }
> = ({ button, forceStickyState, idAttribute, navItems }) => {
  const { asPath } = useRouter();
  const { getLocalizedPathnamesMatch } = useParseLocalizedPathname();
  const { activeAnchorId, getAnchorElement } = useNavBarContext();
  const { sticking } = useSticking();
  const isStickyState = forceStickyState || sticking;
  const showStickyShadow = sticking;
  const tabsRef = React.useRef<HTMLDivElement>(null);
  const navItemsWithAnchors = React.useMemo(
    () =>
      navItems.map((item, index) => ({
        ...item,
        anchorId: getAnchorIdFromHref(item.href, asPath, getLocalizedPathnamesMatch),
        index
      })),
    [asPath, getLocalizedPathnamesMatch, navItems]
  );
  const selectedTabId =
    navItemsWithAnchors.find((item) => item.anchorId && item.anchorId === activeAnchorId)?.id ||
    navItemsWithAnchors.find(
      (item) => !item.anchorId && getLocalizedPathnamesMatch(item.href, asPath)
    )?.id;
  const items = React.useMemo(
    () =>
      navItemsWithAnchors.map(({ anchorId, ...item }) => ({
        ...item,
        href: anchorId ? undefined : item.href
      })),
    [navItemsWithAnchors]
  );

  React.useEffect(() => {
    if (!selectedTabId || !tabsRef.current) {
      return;
    }

    const activeTab = tabsRef.current.querySelector<HTMLElement>(
      "[role='tab'][aria-selected='true']"
    );

    if (!activeTab) {
      return;
    }

    activeTab.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center"
    });
  }, [selectedTabId]);

  const handleClickTab = React.useCallback(
    (tabId: string) => {
      const item = navItemsWithAnchors.find((navItem) => navItem.id === tabId);

      if (!item?.anchorId) {
        return;
      }

      const anchorElement = getAnchorElement(item.anchorId);

      if (!anchorElement) {
        return;
      }

      const top =
        anchorElement.getBoundingClientRect().top +
        globalThis.scrollY -
        (item.index === 0 ? FIRST_ANCHOR_OFFSET : ANCHOR_OFFSET);

      globalThis.scrollTo({
        top,
        behavior: "smooth"
      });
    },
    [getAnchorElement, navItemsWithAnchors]
  );

  return (
    <Box
      data-scroll={idAttribute}
      as="nav"
      aria-label="Secondary navigation"
      sx={{
        backgroundColor: ["white", null, isStickyState ? "white" : "transparent"],
        boxShadow: showStickyShadow ? "shadow-100" : "none",
        borderColor: [
          isStickyState ? "stroke-neutral" : "stroke-neutral-tertiary",
          null,
          isStickyState ? "stroke-neutral" : "transparent"
        ],
        borderStyle: "solid",
        borderWidth: "1px 0",
        left: 0,
        position: ["relative", "relative", forceStickyState ? "relative" : "absolute"],
        top: [0, 0, forceStickyState ? 0 : -9],
        width: "100%"
      }}
    >
      {button && isStickyState && (
        <Box
          sx={{
            borderBottom: "1px solid",
            borderColor: "stroke-neutral-tertiary",
            display: ["block", null, "none"],
            py: 4
          }}
        >
          <Grid variant="uniform-12">
            <GridColumn>{button}</GridColumn>
          </Grid>
        </Box>
      )}
      <Grid variant="uniform-12">
        <GridColumn
          sx={{
            alignItems: ["left", null, isStickyState ? "left" : "center"],
            flexDirection: isStickyState ? "row" : "column"
          }}
        >
          <Box
            sx={{
              backgroundColor: ["transparent", null, isStickyState ? "transparent" : "white"],
              borderColor: "stroke-neutral",
              borderRadius: ["unset", null, isStickyState ? "unset" : "radius-2"],
              borderStyle: "solid",
              borderWidth: [0, null, isStickyState ? 0 : "1px"],
              boxShadow: ["none", null, isStickyState ? "none" : "shadow-100"],
              px: ["unset", null, isStickyState ? "unset" : "spacing-7"],
              py: "spacing-4",
              width: ["unset", null, "fit-content"]
            }}
          >
            <PageHeaderScrollable>
              <Box
                ref={tabsRef}
                sx={{
                  display: "inline-flex",
                  minWidth: "100%",
                  whiteSpace: "nowrap"
                }}
              >
                <Tabs
                  baseline={false}
                  items={items}
                  selectedTabId={selectedTabId}
                  onClickTab={handleClickTab}
                />
              </Box>
            </PageHeaderScrollable>
          </Box>
          {button && isStickyState && (
            <Box
              sx={{
                display: ["none", null, "block"],
                marginInlineStart: "auto",
                mt: 3
              }}
            >
              {button}
            </Box>
          )}
        </GridColumn>
      </Grid>
    </Box>
  );
};

export const NavBar: React.FC<ContentComponentProps<NavBarContent>> = ({ ...props }) => {
  const idAttribute = `nav-${useId().replace(/[^a-z0-9]/gi, "")}`;

  return (
    <>
      <Box
        id={idAttribute}
        sx={{ position: "relative", top: props.forceStickyState ? 0 : [0, 0, -9] }}
      />
      <Sticky
        bottom={props.forceStickyState ? 0 : NAV_BAR_STICKY_OFFSET}
        top={props.forceStickyState ? 0 : NAV_BAR_STICKY_OFFSET}
      >
        <NavBarWithContext {...props} idAttribute={idAttribute} />
      </Sticky>
    </>
  );
};
